/**
 * English locale resource for the MBIP presentation layer.
 *
 * Keys are semantic and stable; the English value is also the default
 * rendered when a key is missing from another locale (i18next fallback).
 * Business identities (Model, ASIN, SKU, Invoice, PO, Customer Code,
 * Country Code, Currency Code, source filenames) are NEVER part of this
 * resource — they flow through unchanged from the backend DTOs.
 */
export const en: Record<string, string> = {
  // ------------------------------------------------------------------
  // common
  // ------------------------------------------------------------------
  "common.country": "Country",
  "common.model": "Model",
  "common.period": "Period",
  "common.coverage": "Coverage",
  "common.evidence": "Evidence",
  "common.authority": "Authority",
  "common.target": "Target",
  "common.completion": "Completion",
  "common.variance": "Variance",
  "common.snapshot": "Snapshot",
  "common.status": "Status",
  "common.grain": "Grain",
  "common.view": "View",
  "common.channel": "Channel",
  "common.businessContext": "Business context",
  "common.asOf": "As of {{value}}",
  "common.periodOf": "Period {{value}}",
  "common.metricAria": "{{label}} metric",
  "common.refresh": "Refresh",
  "common.retry": "Retry",
  "common.readOnly": "READ ONLY",
  "common.allCountries": "All Countries",
  "common.allModels": "All models",
  "common.allAsins": "All ASINs",
  "common.allSourceSkus": "All source SKUs",
  "common.allExactModels": "All exact models",
  "common.allInvoiceCountries": "All invoice countries",
  "common.unresolvedOnly": "Unresolved only",
  "common.businessYear": "Business year",
  "common.periodGrain": "Period grain",
  "common.issueDatePeriod": "Issue-date period",
  "common.sortRows": "Sort rows",
  "common.sortBackendFacts": "Sort backend facts",
  "common.approximate": "Approx.",
  "common.missing": "Missing",
  "common.unresolved": "Unresolved",
  "common.available": "Available",
  "common.rows": "{{count}} rows",
  "common.exactRows": "{{count}} exact rows",
  "common.exactSourceSkuRows": "{{count}} exact source-SKU rows",
  "common.loading": "Loading",
  "common.language": "Language",
  "common.english": "English",
  "common.chinese": "中文",
  "common.workspace": "{{group}} workspace",
  "common.copiedViewLink": "Current view link copied, including filters in the URL.",
  "common.copyViewLinkFailed": "Could not copy the current view link.",
  "common.onlinePreview": "ONLINE PREVIEW",
  "common.neoUat": "NEO UAT",
  "common.environmentUnverified": "ENVIRONMENT UNVERIFIED",
  "common.onlinePreviewReadOnly": "ONLINE PREVIEW · READ ONLY",
  "common.sourceUatMaintenance": "SOURCE UAT · MAINTENANCE",
  "common.uatReadOnly": "UAT · READ ONLY",
  "common.noSourceTimestamp": "No source timestamp",
  "common.environmentIdentityUnavailable": "Environment identity unavailable",
  "common.shareCurrentView": "Share current view",
  "common.masterData": "Master Data",
  "common.expandNavigation": "Expand navigation",
  "common.collapseNavigation": "Collapse navigation",
  "common.brandTagline": "Business Intelligence",
  "common.homeAria": "MBIP home",
  "common.loadingWorkspace": "Loading workspace",
  "common.skipToContent": "Skip to main content",
  "common.primaryNavigation": "Primary navigation",
  "common.tableControls": "Table controls",
  "common.tableDisplay": "Table display",
  "common.columnWidth": "Column width",
  "common.rowHeight": "Row height",
  "common.tableDisplayHint": "Applies to every table and keeps wide data horizontally scrollable.",
  "common.compact": "Compact",
  "common.standard": "Standard",
  "common.wide": "Wide",
  "common.comfortable": "Comfortable",
  "common.reset": "Reset",

  // ------------------------------------------------------------------
  // navigation
  // ------------------------------------------------------------------
  "navigation.group.global": "Global",
  "navigation.group.amazon": "Amazon",
  "navigation.group.joybuy": "Joybuy",
  "navigation.group.system": "System",
  "navigation.actionCenter": "Action Center",
  "navigation.globalSales": "Global Sales",
  "navigation.sellOut": "Sell-Out",
  "navigation.planningInventory": "Planning & Inventory",
  "navigation.cost": "Cost",
  "navigation.globalCost": "Global Cost",
  "navigation.profitability": "Profitability",
  "navigation.globalProfitability": "Global Profitability",
  "navigation.invoiceReconciliation": "Invoice Reconciliation",
  "navigation.sales": "Sales",
  "navigation.inventory": "Inventory",
  "navigation.promotion": "Promotion",
  "navigation.rebateSettlement": "Rebate & Settlement",
  "navigation.operation": "Operation",
  "navigation.dataUpload": "Data Upload",
  "navigation.orderPlanning": "Order Planning",
  "navigation.dataSources": "Data Sources",
  "navigation.dataSourcesIdentity": "Data Sources & Identity",
  "navigation.masterData": "Master Data",
  "navigation.backendConnectivity": "Backend Connectivity",
  "navigation.dataImportCenter": "Data Import Center",
  "navigation.amazonSales": "Amazon Sales",
  "navigation.amazonInventory": "Amazon Inventory",
  "navigation.amazonPromotion": "Amazon Promotion",
  "navigation.amazonRebateSettlement": "Amazon Rebate & Settlement",
  "navigation.amazonOperation": "Amazon Operation",
  "navigation.amazonDataUpload": "Amazon Data Upload",
  "navigation.joybuySales": "Joybuy Sales",
  "navigation.joybuyInventory": "Joybuy Inventory",
  "navigation.joybuyOrderPlanning": "Joybuy Order Planning",
  "navigation.joybuyOperation": "Joybuy Operation",
  "navigation.joybuyTraffic": "Joybuy Traffic",
  "navigation.joybuyDataUpload": "Joybuy Data Upload Center",

  // Global Cost — HQ Model EXW movement
  "cost.context": "Global · authoritative monthly evidence",
  "cost.title": "Cost",
  "cost.description": "HQ Model EXW movement and monthly Cost evidence. Inventory Cost remains a separate authority.",
  "cost.hqModel": "HQ Model",
  "cost.exwMovement": "HQ Model EXW Movement",
  "cost.exwHistory": "EXW Cost History",
  "cost.latestMonth": "Latest Month",
  "cost.currency": "Currency",
  "cost.unit": "Unit",
  "cost.memberCoverage": "Member Coverage",
  "cost.mixedMemberExw": "Mixed Member EXW",
  "cost.multipleScopes": "Multiple currency/unit scopes",
  "cost.hqAuthorityDescription": "Exact approved Global Cost group membership; no string or fuzzy grouping is performed in the browser.",
  "cost.noHqAuthority": "No approved HQ Model authority",
  "cost.noHqAuthorityDescription": "The result is unresolved; no family is inferred.",
  "cost.latestPeriod": "Latest Period",
  "cost.previousPeriod": "Previous Period",
  "cost.latestExw": "Latest EXW",
  "cost.previousExw": "Previous EXW",
  "cost.absoluteChange": "Absolute Change",
  "cost.percentageChange": "Percentage Change",
  "cost.largestIncrease": "Largest Increase",
  "cost.largestDecrease": "Largest Decrease",
  "cost.memberCount": "Commercial Models",
  "cost.exceptionCount": "Exception Types",
  "cost.familyComparison": "Latest Comparable Family Range",
  "cost.familyComparisonDescription": "Min, max and absolute spread are backend-calculated only within the same month, currency and unit.",
  "cost.membershipEvidence": "HQ Model Membership Evidence",
  "cost.exwEvidence": "EXW Movement Evidence",
  "cost.exwMissing": "EXW missing",
  "cost.exwMissingDescription": "Missing is not converted to zero and is excluded from ranking.",
  "cost.inventoryHistory": "Inventory Cost History",
  "cost.inventorySeparate": "Inventory Cost is presented separately and is never overlaid or substituted for EXW.",
  "cost.hqExpansionTitle": "HQ Family Expansion Confirmation",
  "cost.hqExpansionScope": "Review exact retained members",
  "cost.hqExpansionDescription": "Choose approved Master Models explicitly. No prefix, similarity, fuzzy match, or frontend suggestion creates membership.",
  "cost.hqExpansionEmpty": "No approved HQ families",
  "cost.hqEffectiveDate": "Effective Date",
  "cost.hqRetainedModels": "Retained Master Models",
  "cost.hqSelectExactModels": "Search and explicitly select Master Models",
  "cost.hqAdded": "Models to Add",
  "cost.hqRemoved": "Models to Remove",
  "cost.hqFactsChanged": "Raw Cost Facts Changed",
  "cost.hqReviewConfirmation": "Review Confirmation",
  "cost.hqReadOnlySelection": "Selection preview is available; confirmation requires the controlled UAT write boundary and audit actor.",
  "cost.hqConfirmTitle": "Confirm HQ Family Members",
  "cost.hqConfirmAction": "Confirm Exact Members",
  "cost.hqConfirmWarning": "Effective-dated authority change",
  "cost.hqConfirmDescription": "This changes only approved HQ membership. Historical Cost facts remain immutable, removed memberships retain history, and the backend revalidates every exact Master Model.",

  // ------------------------------------------------------------------
  // status
  // ------------------------------------------------------------------
  "status.complete": "Complete",
  "status.partial": "Partial",
  "status.missing": "Missing",
  "status.unresolved": "Unresolved",
  "status.derived": "Derived",
  "status.approximate": "Approximate",
  "status.stale": "Stale",
  "status.blocked": "Blocked",
  "status.reconciled": "Reconciled",
  "status.available": "Available",
  "status.matched": "Matched",
  "status.exact": "Exact",
  "status.healthy": "Healthy",
  "status.calculated": "Calculated",
  "status.low": "Low",
  "status.high": "High",
  "status.excess": "Excess",
  "status.critical": "Critical",
  "status.source": "Source",
  "status.zero": "Zero",

  "status.targetUnavailable": "Target unavailable",
  "status.revenueMissing": "Revenue missing",
  "status.revenueCoveragePartial": "Revenue coverage partial",
  "status.customerFactsComplete": "Customer facts complete",
  "status.outsideStandardCustomerAggregate": "Outside Standard Customer aggregate",
  "status.noScopedExceptions": "No scoped exceptions",
  "status.noScopedInvoiceFacts": "No scoped invoice facts",
  "status.standardCustomerScope": "Standard Customer Scope",
  "status.freeOfCharge": "Free of charge",
  "status.realZero": "Real zero",
  "status.realZeroFactPresent": "Real zero fact present",
  "status.standardSalesFacts": "Standard sales facts",
  "status.signedSalesAdjustmentPresent": "Signed sales adjustment present",
  "status.approximateFx": "Approximate FX",
  "status.fxRequired": "FX required",
  "status.revenueUnavailable": "Revenue unavailable",
  "status.resolved": "Resolved",
  "status.reviewRequired": "Review required",
  "status.mtdPartial": "MTD / Partial",
  "status.closedPeriod": "Closed Period",
  "status.mtdPartialPeriod": "MTD / Partial Period",
  "status.future": "Future",
  "status.closed": "Closed",
  "status.notApplicable": "Not applicable",
  "status.noConfirmedSourceExceptions": "No confirmed source exceptions in this context",
  "status.noConfirmedSourceException": "No confirmed source exception in the selected context",
  "status.weeklyAuthoritative": "Weekly authoritative",
  "status.weeklySourceUnavailable": "Weekly source unavailable",
  "status.monthlySourcePending": "Monthly source pending",
  "status.finalAuthoritative": "Final / authoritative",
  "status.monthlySourceUnavailable": "Monthly source unavailable",
  "status.backendComparisonAvailable": "Backend comparison available",
  "status.priorPeriodZero": "Prior period is zero",
  "status.priorPeriodMissing": "Prior period unavailable",
  "status.currentPeriodMissing": "Current period unavailable",

  // ------------------------------------------------------------------
  // metrics (core commercial BI terminology)
  // ------------------------------------------------------------------
  "metrics.sellOut": "Sell-out",
  "metrics.sellOutUnits": "Sell-out Units",
  "metrics.sellIn": "Sell-in",
  "metrics.invoicedSellIn": "Invoiced Sell-in",
  "metrics.invoicedSellInUnits": "Invoiced Sell-in Units",
  "metrics.customerOnHand": "Customer On Hand",
  "metrics.inTransit": "In Transit",
  "metrics.conversion": "Conversion",
  "metrics.traffic": "Traffic",
  "metrics.invoicedUnits": "Invoiced Units",
  "metrics.invoicedRevenue": "Invoiced Revenue",
  "metrics.invoiceDocuments": "Invoice Documents",
  "metrics.invoiceLines": "Invoice Lines",
  "metrics.revenue": "Revenue",
  "metrics.woc": "WOC (30d)",
  "metrics.marketplaceInventory": "Marketplace Inventory",
  "metrics.panEuStock": "Pan-EU Stock",
  "metrics.currentPo": "Current PO",
  "metrics.openPo": "Open PO",
  "metrics.poIntake": "PO Intake",
  "metrics.invoiceCompletion": "Invoice Completion",
  "metrics.netChannelFlow": "Net Channel Flow",
  "metrics.alignment3m": "3M Sell-through Alignment",
  "metrics.invoicedAmount": "Invoiced Amount",
  "metrics.unresolvedTransit": "Unresolved Transit",
  "metrics.actualUnits": "Actual Units",
  "metrics.targetUnits": "Target Units",
  "metrics.unitsCompletion": "Units Completion",
  "metrics.actualRevenue": "Actual Revenue",
  "metrics.targetRevenue": "Target Revenue",
  "metrics.revenueCompletion": "Revenue Completion",
  "metrics.actualRevenueCoverage": "Actual Revenue Coverage",
  "metrics.targetRevenueCoverage": "Target Revenue Coverage",
  "metrics.reportingFx": "Reporting FX",
  "metrics.customerScope": "Customer Scope",
  "metrics.trafficUv7d": "Traffic · UV 7d",
  "metrics.sellOut7d": "Sell-out · 7d",
  "metrics.conversion7d": "Conversion · 7d",
  "metrics.sourceTraffic": "Source Traffic",
  "metrics.trafficStatus": "Traffic Status",
  "metrics.conversionOrdersTraffic": "Conversion · Orders / Traffic",
  "metrics.conversionStatus": "Conversion Status",
  "metrics.sourceConversionRatio": "Source Conversion Ratio",
  "metrics.sellOutStatus": "Sell-out Status",
  "metrics.mapping": "Mapping",
  "metrics.source": "Source",
  "metrics.weekComparison": "Week comparison",
  "metrics.monthOverMonth": "Month over Month",
  "metrics.yearOverYear": "Year over Year",

  // ------------------------------------------------------------------
  // authority
  // ------------------------------------------------------------------
  "authority.joybuyOfficialPdfInvoice": "Canonical Sales Invoice Authority",
  "authority.amazonWeeklySales": "Weekly Sales",
  "authority.amazonMonthlySales": "Monthly Sales",
  "authority.amazonMtdWeeklySales": "MTD · Weekly Sales",
  "authority.activeInvoiceLines": "Active Invoice Lines",
  "authority.customerSalesTarget": "Customer Sales Target",
  "authority.backendCustomerAttainment": "Backend Customer Attainment",
  "authority.invoiceRevenueEurNormalized": "Invoice Revenue · EUR normalized",
  "authority.customerRevenueTarget": "Customer Revenue Target",
  "authority.businessResolvedInvoiceLines": "Business-resolved Invoice Lines",
  "authority.approximatePlnEurReportingFx": "Approximate PLN→EUR Reporting FX",
  "authority.standardCustomerKpiScope": "Standard Customer KPI Scope",
  "authority.activeInvoiceLinesDivTarget": "Active Invoice Lines ÷ Customer Sales Target",
  "authority.normalizedInvoiceRevenueDivTarget": "Normalized Invoice Revenue ÷ Customer Revenue Target",
  "authority.currentAcceptedAmazonSourceVersions": "Current accepted Amazon source versions",
  "authority.weeklyOperation": "Weekly Operation",
  "authority.acceptedAmazonStockPoCompletion": "Accepted Amazon stock + exact PO completion",
  "authority.amazonPurchaseOrders": "Amazon Purchase Orders",
  "authority.activeAmazonInvoices": "Active Amazon Invoices",
  "authority.backendInvoicedSellInMinusSellOut": "Backend: Invoiced Sell-in − Sell-out",
  "authority.currentPoMinusSignedInvoiceCompletion": "Current PO − exact signed Invoice completion",
  "authority.backendRolling3ClosedMonths": "Backend · Rolling 3 Closed Months",
  "authority.activeInvoiceHeadersSummary": "Active Invoice headers + Amazon Invoice Summary",
  "authority.backendExactComparableScope": "Backend · exact comparable scope",
  "authority.officialPdfInvoices": "Canonical Sales Invoice Authority",
  "authority.joybuyOperationalSellOut": "Joybuy Operational Sell-out",
  "authority.sourceRevenue": "{{authority}} · Source Revenue",
  "authority.periodComparison": "Backend period comparison",

  // ------------------------------------------------------------------
  // shared BI component chrome
  // ------------------------------------------------------------------
  "bi.authority": "Authority",
  "bi.coverage": "Coverage",
  "bi.target": "Target",
  "bi.completion": "Completion",
  "bi.variance": "Variance",
  "bi.asOf": "As of {{value}}",
  "bi.period": "Period {{value}}",
  "bi.exceptions": "{{count}} exceptions",
  "bi.evidenceSuffix": "evidence",
  "bi.businessContext": "Business context",
  "bi.metricAria": "{{label}} metric",
  "bi.backendComparison": "Backend comparison",
  "bi.backendDelta": "Backend delta",
  "bi.netFlow": "Net Flow",
  "bi.rowCoverage": "Row coverage",
  "bi.productCoverage": "Product coverage",
  "bi.sourceSkuCoverage": "Source-SKU coverage",
  "bi.salesInCoverage": "Sell-in coverage",
  "bi.salesOutCoverage": "Sell-out coverage",
  "bi.comparableScope": "Comparable scope",
  "bi.productUnresolved": "{{count}} product unresolved",
  "bi.rowsResolved": "{{count}} / {{count2}} rows resolved",
  "bi.rowsResolvedShort": "{{count}} / {{count2}} rows",
  "bi.coverageText": "Coverage",
  "bi.feedbackLoading": "Loading",
  "bi.feedbackLoadingDescription": "Preparing the latest authoritative information.",
  "bi.feedbackNoResults": "No results",
  "bi.feedbackNoResultsDescription": "No information is available for this view.",
  "bi.feedbackUnavailable": "Unavailable",
  "bi.feedbackUnavailableDescription": "The backend has not supplied this information.",
  "bi.feedbackError": "Unable to load this view",
  "bi.feedbackErrorDescription": "Retry the read-only request.",
  "bi.feedbackTechnicalDetail": "Technical detail",
  "bi.feedbackPartialCoverage": "Partial coverage",
  "bi.feedbackPartialCoverageDescription": "Some backend facts remain missing or unresolved.",
  "bi.refreshRefreshing": "Refreshing…",
  "bi.refreshUpdated": "Updated just now",
  "bi.refreshNoop": "Already up to date",
  "bi.refreshPartial": "Updated with partial coverage",
  "bi.refreshFailed": "Refresh failed",
  "bi.missingGlyph": "—",

  // ------------------------------------------------------------------
  // charts
  // ------------------------------------------------------------------
  "chart.units": "Units",
  "chart.weeks": "Weeks",
  "chart.eur": "EUR",
  "chart.visitors": "Visitors",
  "chart.rate": "Rate",
  "chart.sellOutUnits": "Sell-out Units",
  "chart.invoicedUnits": "Invoiced Units",
  "chart.invoicedRevenueEur": "Invoiced Revenue EUR",
  "chart.customerOnHand": "Customer On Hand",
  "chart.inTransit": "In Transit",
  "chart.woc": "WOC",
  "chart.actual": "Actual",
  "chart.target": "Target",
  "chart.marketplaceInventory": "Marketplace Inventory",
  "chart.panEuStock": "Pan-EU Stock",
  "chart.poIntake": "PO Intake",
  "chart.invoicedSellIn": "Invoiced Sell-in",
  "chart.sellOut": "Sell-out",
  "chart.trafficUv7d": "Traffic · UV 7d",
  "chart.sellOut7d": "Sell-out · 7d",
  "chart.conversionOrdersTraffic": "Conversion · Orders / Traffic",
  "chart.country": "Country",
  "chart.model": "Model",

  // ------------------------------------------------------------------
  // global sales page
  // ------------------------------------------------------------------
  "pages.globalSales.breadcrumb": "Global · Commercial BI",
  "pages.globalSales.title": "Global Sales Performance",
  "pages.globalSales.description": "Invoice-authoritative financial Sales and customer targets, with RN operational Sell-in shown separately.",
  "pages.globalSales.ariaPeriod": "Global Sales Period",
  "pages.globalSales.ariaCustomer": "Global Sales Customer",
  "pages.globalSales.ariaCountry": "Global Sales Country",
  "pages.globalSales.ariaModel": "Global Sales Model",
  "pages.globalSales.ariaSort": "Customer Target Sort",
  "pages.globalSales.contextAria": "Global Sales business context",
  "pages.globalSales.targetGrainUnavailableTitle": "Target grain unavailable for this filter",
  "pages.globalSales.targetGrainUnavailableDescription": "Country/model-filtered Actual remains authoritative. Customer-period Target and Completion remain Missing and are not allocated.",
  "pages.globalSales.outOfScopeTitle": "Customer is outside Standard Customer completion",
  "pages.globalSales.outOfScopeDescription": "Its source and target evidence remains available below; aggregate completion stays Missing.",
  "pages.globalSales.unitsPerformance": "Units Performance",
  "pages.globalSales.unitsPerformanceDescription": "Completion first, followed by co-scoped Invoice Actual and Customer Target.",
  "pages.globalSales.completionVsTime": "Completion vs Time Progress",
  "pages.globalSales.completionVsTimeDescription": "Sales completion versus elapsed calendar progress. Pace Gap is a benchmark, not a forecast.",
  "pages.globalSales.timeProgress": "Time Progress",
  "pages.globalSales.paceGap": "Pace Gap",
  "pages.globalSales.revenuePerformance": "Revenue Performance",
  "pages.globalSales.revenuePerformanceDescription": "EUR reporting values with independent Target and FX coverage.",
  "pages.globalSales.actualVsTargetTrend": "Actual vs Target Trend",
  "pages.globalSales.actualVsTargetTrendDescription": "Only series supported by current backend authority are enabled.",
  "pages.globalSales.unitsTrend": "Units trend",
  "pages.globalSales.unitsTrendDetail": "Actual series exists at customer-period grain. Actual-vs-Target trend remains available in the selected customer evidence drawer; no top-level Target series is exposed.",
  "pages.globalSales.revenueTrend": "Revenue trend",
  "pages.globalSales.revenueTrendUnavailable": "Target trend unavailable",
  "pages.globalSales.revenueTrendDetail": "No top-level normalized Actual/Target revenue series exists. Annual Target is not divided across periods.",
  "pages.globalSales.customerDriverAnalysis": "Customer Driver Analysis",
  "pages.globalSales.customerDriverAnalysisDescription": "Customer-period is the authoritative Target grain. Default ranking uses the largest backend Remaining Gap.",
  "pages.globalSales.sortLargestGap": "Largest Remaining Gap",
  "pages.globalSales.sortLowestAttainment": "Lowest Completion",
  "pages.globalSales.sortLargestSales": "Largest Actual Units",
  "pages.globalSales.sortHighestAttainment": "Highest Completion",
  "pages.globalSales.countryModelContribution": "Country & Model Actual Contribution",
  "pages.globalSales.countryModelContributionDescription": "These dimensions support authoritative filtered Actual only. Target allocation and completion are unavailable at the current Target grain.",
  "pages.globalSales.noTargetAllocation": "No Country / Model Target Allocation",
  "pages.globalSales.noTargetAllocationDescription": "Select filters to inspect Invoice Actual. Customer Target is never allocated by Actual share in React.",
  "pages.globalSales.dataQualityScope": "Data Quality & Scope",
  "pages.globalSales.dataQualityScopeDescription": "Coverage, FX and scope remain visible without competing with primary business outcomes.",
  "pages.globalSales.exceptions": "Sales Data Quality & Exceptions",
  "pages.globalSales.exceptionsDescription": "Deterministic current facts only; formal ownership and workflow are deferred to BI-006.",
  "pages.globalSales.salesEvidence": "Sales Evidence",
  "pages.globalSales.salesEvidenceDescription": "Invoice reconciliation evidence is not loaded into the RN summary and never replaces RN attainment.",
  "pages.globalSales.noPlnEvidence": "No PLN evidence in selected scope",
  "pages.globalSales.loading": "Loading Global Sales",
  "pages.globalSales.loadingDescription": "Reading RN-authoritative operational Sell-in, governed customer targets and Invoice-authoritative financial evidence.",
  "pages.globalSales.unavailable": "Global Sales unavailable",
  "pages.globalSales.loadingCustomerComparison": "Loading customer comparison",
  "pages.globalSales.loadingCustomerTrend": "Loading customer trend",
  "pages.globalSales.chartAriaCustomerTarget": "Customer Actual versus Target ranked by selected business field",
  "pages.globalSales.colCustomer": "Customer",
  "pages.globalSales.colCountry": "Country",
  "pages.globalSales.colActualUnits": "RN Units",
  "pages.globalSales.colTargetUnits": "Target Units",
  "pages.globalSales.colUnitsVariance": "Units Variance",
  "pages.globalSales.colUnitsCompletion": "Units Completion",
  "pages.globalSales.colActualRevenue": "Actual Revenue",
  "pages.globalSales.colTargetRevenue": "Target Revenue",
  "pages.globalSales.colRevenueCompletion": "Revenue Completion",
  "pages.globalSales.colCoverageScope": "Coverage / Scope",
  "pages.globalSales.colEvidence": "Evidence",
  "pages.globalSales.viewEvidence": "View evidence",
  "pages.globalSales.drawerTitle": "{{customer}} · Sales evidence",
  "pages.globalSales.drawerFallbackTitle": "Customer detail",
  "pages.globalSales.drawerTrendAria": "{{customer}} Actual and Target period trend",
  "pages.globalSales.countryMarketActual": "Country / market Actual contribution",
  "pages.globalSales.colSourceIdentities": "Source identities",
  "pages.globalSales.colInvoiceLines": "Invoice lines",
  "pages.globalSales.invoiceSourceIdentities": "Invoice source identities",
  "pages.globalSales.colSourceCode": "Source code",
  "pages.globalSales.colSourceLegalCustomer": "Source / legal customer",
  "pages.globalSales.colDocumentsLines": "Documents / Lines",
  "pages.globalSales.colInvoiceEvidence": "Invoice evidence",
  "pages.globalSales.targetComponents": "Target components",
  "pages.globalSales.monthValues": "{{count}} month values",
  "pages.globalSales.targetUnavailable": "Target unavailable",
  "pages.globalSales.fxRate": "1 PLN = {{rate}} EUR",
  "pages.globalSales.fxRateUnavailable": "Rate unavailable",
  "pages.globalSales.approximatePlnEur": "Approximate PLN→EUR",
  "pages.globalSales.fxUnavailable": "FX unavailable",
  "pages.globalSales.standardCustomers": "Standard Customers",
  "pages.globalSales.allCustomers": "All customers",
  "pages.globalSales.selectedCustomer": "Selected customer",
  "pages.globalSales.allCountries": "All countries",
  "pages.globalSales.allModels": "All models",
  "pages.globalSales.selectedModel": "Selected model",
  "pages.globalSales.grainYear": "Year",
  "pages.globalSales.grainQuarter": "Quarter",
  "pages.globalSales.grainMonth": "Month",
  "pages.globalSales.selectedCountryActual": "Selected Country Actual Units",
  "pages.globalSales.selectedModelActual": "Selected Model Actual Units",
  "pages.globalSales.countryActualHint": "Target grain unavailable; select a country for Actual contribution.",
  "pages.globalSales.modelActualHint": "Target grain unavailable; select a model for Actual contribution.",
  "pages.globalSales.qualityDetailFreeOfCharge": "{{count}} free-of-charge · {{missing}} missing",
  "pages.globalSales.qualityDetailMissingTarget": "{{count}} missing target rows",
  "pages.globalSales.qualityDetailFx": "{{rate}} · {{count}} PLN lines",
  "pages.globalSales.qualityDetailScope": "{{count}} excluded · evidence retained",
  "pages.globalSales.exceptionApproxFx": "Approximate reporting FX",
  "pages.globalSales.exceptionApproxFxDetail": "{{count}} PLN lines use the configured reporting rate.",
  "pages.globalSales.exceptionExcludedCustomers": "Customers outside aggregate completion",
  "pages.globalSales.exceptionExcludedCustomersDetail": "{{count}} customers remain available for evidence review.",
  "pages.globalSales.exceptionTargetGrain": "Target grain unavailable",
  "pages.globalSales.exceptionTargetGrainDetail": "Country/model-filtered Actual is authoritative; Target and Completion remain Missing.",
  "pages.globalSales.exceptionRevenueMissing": "Revenue facts missing",
  "pages.globalSales.exceptionRevenueMissingDetail": "{{count}} Invoice lines remain Missing.",
  "pages.globalSales.exceptionTargetRevenueMissing": "Target revenue missing",
  "pages.globalSales.exceptionTargetRevenueMissingDetail": "{{count}} target rows remain Missing.",
  "pages.globalSales.evidenceViewCustomerTarget": "View customer target evidence",
  "pages.globalSales.evidenceViewRevenue": "View revenue evidence",
  "pages.globalSales.customerFilterLabel": "Customer",
  "pages.globalSales.countryFilterLabel": "Country",
  "pages.globalSales.modelFilterLabel": "Model",
  "pages.globalSales.periodFilterLabel": "Period",
  "pages.globalSales.customerScopeLabel": "Scope",
  "pages.globalSales.salesOutcomesAria": "Global Sales business outcomes",
  "pages.globalSales.colInvoiceNumber": "Invoice",
  "pages.globalSales.colInvoiceDate": "Invoice Date",
  "pages.globalSales.colUnits": "Units",
  "pages.globalSales.colRevenuePln": "Revenue PLN",
  "pages.globalSales.colEurReporting": "EUR Reporting",
  "pages.globalSales.colFxStatus": "FX Status",
  "authority.backendCalendarReference": "Backend calendar reference date",
  "authority.backendCompletionMinusTime": "Backend Completion − Time Progress",

  // ------------------------------------------------------------------
  // amazon sales page
  // ------------------------------------------------------------------
  "pages.amazonSales.viewTitle": "Amazon Sales View",
  "pages.amazonSales.viewDescription": "Sell-out performance and Channel Flow retain independent reporting state.",
  "pages.amazonSales.sellOutPerformance": "Sell-out Performance",
  "pages.amazonSales.channelFlow": "Channel Flow",
  "pages.amazonSales.ariaView": "Amazon Sales View",
  "pages.amazonSales.salesOutcomesAria": "Amazon Sales outcomes",
  "pages.amazonSales.breadcrumb": "Amazon · Commercial BI",
  "pages.amazonSales.title": "Amazon Sales Performance",
  "pages.amazonSales.description": "Sell-out outcomes, period context, country and exact Model / ASIN drivers from one source authority.",
  "pages.amazonSales.ariaYear": "Amazon Sales Year",
  "pages.amazonSales.ariaGrain": "Amazon Sales Period Grain",
  "pages.amazonSales.ariaPeriod": "Amazon Sales Period",
  "pages.amazonSales.ariaCountry": "Amazon Sales Country",
  "pages.amazonSales.ariaModel": "Amazon Sales Model",
  "pages.amazonSales.ariaDriverSort": "Amazon Sales Driver Sort",
  "pages.amazonSales.reportingWeek": "Reporting week",
  "pages.amazonSales.contextAria": "Amazon Sales business context",
  "pages.amazonSales.mtdAlertTitle": "MTD · Weekly Sales",
  "pages.amazonSales.mtdAlertDescription": "Monthly source pending. KPI, trend point, country and model drivers all use the same backend-selected Weekly MTD authority.",
  "pages.amazonSales.salesOutcomes": "Sales Outcomes",
  "pages.amazonSales.salesOutcomesDescription": "Results lead the page. No Sales Target or Completion is manufactured for Amazon Sales.",
  "pages.amazonSales.periodTrend": "Period Trend",
  "pages.amazonSales.periodTrendWeekDescription": "Selected Week scopes KPI and drivers; the trend retains every authoritative reporting week.",
  "pages.amazonSales.periodTrendMonthDescription": "Monthly authority forms Month, Quarter and Year trend points. Weekly and Monthly sources are never mixed.",
  "pages.amazonSales.loadingTrend": "Loading Amazon Sales trend",
  "pages.amazonSales.trendAria": "Amazon Sell-out authoritative period trend",
  "pages.amazonSales.colReportingPeriod": "Reporting Period",
  "pages.amazonSales.colSellOutUnits": "Sell-out Units",
  "pages.amazonSales.colRevenue": "Revenue",
  "pages.amazonSales.colSource": "Source",
  "pages.amazonSales.colCoverage": "Coverage",
  "pages.amazonSales.countryDrivers": "Country Drivers",
  "pages.amazonSales.countryDriversDescription": "Selected-period backend facts. Zero, signed values and Missing remain distinct.",
  "pages.amazonSales.loadingCountryDrivers": "Loading country drivers",
  "pages.amazonSales.countryDriversAria": "Amazon Sell-out country drivers",
  "pages.amazonSales.colCountry": "Country",
  "pages.amazonSales.colUnits": "Units",
  "pages.amazonSales.colFactStatus": "Fact Status",
  "pages.amazonSales.colSourceCoverage": "Source Coverage",
  "pages.amazonSales.colEvidence": "Evidence",
  "pages.amazonSales.sourceRows": "Source rows",
  "pages.amazonSales.modelDrivers": "Model / ASIN Drivers",
  "pages.amazonSales.modelDriversDescription": "Exact backend master model identity with retained ASIN evidence; no fuzzy or first-candidate mapping.",
  "pages.amazonSales.loadingModelDrivers": "Loading Model / ASIN drivers",
  "pages.amazonSales.modelDriversAria": "Amazon Sell-out Model and ASIN drivers",
  "pages.amazonSales.colModel": "Model",
  "pages.amazonSales.colAsin": "ASIN",
  "pages.amazonSales.sortHighestUnits": "Highest Units",
  "pages.amazonSales.sortHighestRevenue": "Highest Revenue",
  "pages.amazonSales.sortIdentity": "Identity",
  "pages.amazonSales.sourceAuthorityQuality": "Source Authority & Data Quality",
  "pages.amazonSales.sourceAuthorityQualityDescription": "Compact source quality metadata remains subordinate to business outcomes.",
  "pages.amazonSales.exceptions": "Amazon Sales Exceptions",
  "pages.amazonSales.exceptionsDescription": "Confirmed source conditions only; signed adjustments are retained facts, not automatically classified as errors.",
  "pages.amazonSales.evidence": "Evidence",
  "pages.amazonSales.evidenceDescription": "KPI, Country and Model facts resolve to the selected reporting period and current accepted source versions.",
  "pages.amazonSales.sourceUploadHistory": "Source upload history",
  "pages.amazonSales.identityEvidence": "Identity evidence",
  "pages.amazonSales.batchIdentitiesHint": "Batch identifiers stay in the governed upload history rather than the main BI page.",
  "pages.amazonSales.loading": "Loading Amazon Sales",
  "pages.amazonSales.loadingDescription": "Reading one authoritative sales source for the selected reporting context.",
  "pages.amazonSales.unavailable": "Amazon Sales unavailable",
  "pages.amazonSales.contextChannel": "Channel",
  "pages.amazonSales.contextGrain": "Period Grain",
  "pages.amazonSales.contextPeriod": "Selected Period",
  "pages.amazonSales.contextWeek": "Reporting Week",
  "pages.amazonSales.contextCountry": "Country",
  "pages.amazonSales.contextModel": "Model",
  "pages.amazonSales.contextAuthority": "Source Authority",
  "pages.amazonSales.contextSourceStatus": "Source Status",
  "pages.amazonSales.contextCoverage": "Source Coverage",
  "pages.amazonSales.weekEnding": "Week ending {{period}}",
  "pages.amazonSales.startUnavailable": "Start unavailable",
  "pages.amazonSales.coverageRowsResolved": "{{count}} / {{count2}} rows resolved",
  "pages.amazonSales.sourceStatusMetric": "Source Status",
  "pages.amazonSales.qualityProductResolution": "Product Resolution",
  "pages.amazonSales.qualityProductResolutionDetail": "{{count}} unresolved source rows",
  "pages.amazonSales.qualitySourceBatches": "Source Batches",
  "pages.amazonSales.qualitySourceBatchesDetail": "{{count}} accepted current source rows",
  "pages.amazonSales.qualitySignedFacts": "Signed Facts",
  "pages.amazonSales.qualitySignedFactsDetail": "Legitimate signed adjustments remain signed.",
  "pages.amazonSales.qualityRealZeroFacts": "Real-zero Facts",
  "pages.amazonSales.qualityRealZeroFactsDetail": "Authoritative zero remains distinct from Missing.",
  "pages.amazonSales.exceptionMonthlyPending": "Monthly source pending",
  "pages.amazonSales.exceptionMonthlyPendingDetail": "Current-month result uses Weekly Sales MTD only.",
  "pages.amazonSales.exceptionSourceMissing": "Authoritative source unavailable",
  "pages.amazonSales.exceptionSourceMissingDetail": "Missing remains Missing; no alternative closed-period source is introduced.",
  "pages.amazonSales.exceptionUnresolvedProduct": "Unresolved model / ASIN identity",
  "pages.amazonSales.exceptionUnresolvedProductDetail": "{{count}} source rows remain unresolved.",
  "pages.amazonSales.exceptionSignedAdjustment": "Signed sales adjustment present",
  "pages.amazonSales.exceptionSignedAdjustmentDetail": "{{count}} source rows contain authoritative signed adjustments.",
  "pages.amazonSales.weekSourceQuality": "Complete reporting weeks remain intact, including cross-month weeks.",
  "pages.amazonSales.mtdQuality": "Weekly-derived MTD only; Monthly Sales is not added.",
  "pages.amazonSales.monthlyFinalQuality": "Monthly Sales fully supersedes Weekly MTD.",
  "pages.amazonSales.monthlyMissingQuality": "Missing remains Missing; closed months do not use Weekly fallback.",
  "pages.amazonSales.trendSourceWeekly": "Weekly Sales",
  "pages.amazonSales.trendSourceMtd": "MTD · Weekly-derived",
  "pages.amazonSales.trendSourceMonthly": "Monthly Sales",
  "pages.amazonSales.noticeMtdTitle": "MTD · Weekly Sales",
  "pages.amazonSales.noticeMtdDescription": "Source: Weekly Sales · Monthly source pending. Reporting weeks are attributed by their authoritative week end.",
  "pages.amazonSales.noticeMonthlyTitle": "Source: Monthly Sales",
  "pages.amazonSales.noticeMonthlyDescription": "Authoritative Monthly Sales is selected; Weekly Sales is excluded.",
  "pages.amazonSales.noticeMonthlyUnavailableTitle": "Monthly Sales unavailable",
  "pages.amazonSales.noticeMonthlyUnavailableDescription": "Missing remains Missing. Weekly fallback is not applied to closed historical months.",
  "pages.amazonSales.evidenceViewSource": "View source evidence",
  "pages.amazonSales.evidenceViewRevenue": "View revenue evidence",
  "pages.amazonSales.grainWeek": "Week",

  // ------------------------------------------------------------------
  // amazon channel flow
  // ------------------------------------------------------------------
  "pages.amazonChannelFlow.breadcrumb": "Amazon · Commercial BI",
  "pages.amazonChannelFlow.title": "Amazon Channel Flow",
  "pages.amazonChannelFlow.description": "PO Intake → Invoiced Sell-in → Sell-out, with Open PO retained as an as-of position.",
  "pages.amazonChannelFlow.ariaYear": "Channel Flow Year",
  "pages.amazonChannelFlow.ariaGrain": "Channel Flow Period Grain",
  "pages.amazonChannelFlow.ariaPeriod": "Channel Flow Period",
  "pages.amazonChannelFlow.ariaCountry": "Channel Flow Country",
  "pages.amazonChannelFlow.ariaModel": "Channel Flow Model",
  "pages.amazonChannelFlow.ariaCountrySort": "Channel Flow Country Sort",
  "pages.amazonChannelFlow.ariaModelSort": "Channel Flow Model Sort",
  "pages.amazonChannelFlow.contextAria": "Amazon Channel Flow business context",
  "pages.amazonChannelFlow.mtdAlertTitle": "Current Month · MTD / Partial Period",
  "pages.amazonChannelFlow.mtdAlertDescription": "PO uses accepted PO dates, Invoiced Sell-in uses Invoice dates, and Sell-out follows the existing Weekly MTD / Monthly final authority. The 3M alignment excludes this partial month.",
  "pages.amazonChannelFlow.flowOutcomes": "Flow Outcomes",
  "pages.amazonChannelFlow.flowOutcomesDescription": "Primary results compare actual Invoiced Sell-in with Sell-out; PO Intake and Open PO retain their different Flow and Position meanings.",
  "pages.amazonChannelFlow.primaryOutcomes": "Primary Outcomes",
  "pages.amazonChannelFlow.supportingFlow": "Supporting Flow",
  "pages.amazonChannelFlow.asOfPosition": "As-of Position",
  "pages.amazonChannelFlow.invoiceValueEvidence": "Invoice Value Evidence",
  "pages.amazonChannelFlow.flowTrend": "Flow Trend",
  "pages.amazonChannelFlow.flowTrendDescription": "Backend calendar periods on one Units axis. Missing creates a gap, true zero remains zero, and signed facts remain signed.",
  "pages.amazonChannelFlow.loadingTrend": "Loading Channel Flow trend",
  "pages.amazonChannelFlow.trendAria": "Amazon Channel Flow authoritative trend",
  "pages.amazonChannelFlow.colPeriod": "Period",
  "pages.amazonChannelFlow.colStatus": "Status",
  "pages.amazonChannelFlow.colPoIntake": "PO Intake",
  "pages.amazonChannelFlow.colInvoicedSellIn": "Invoiced Sell-in",
  "pages.amazonChannelFlow.colInvoicedAmount": "Invoiced Amount",
  "pages.amazonChannelFlow.colSellOut": "Sell-out",
  "pages.amazonChannelFlow.colNetChannelFlow": "Net Channel Flow",
  "pages.amazonChannelFlow.colOpenPo": "Open PO",
  "pages.amazonChannelFlow.colCoverage": "Coverage",
  "pages.amazonChannelFlow.colEvidence": "Evidence",
  "pages.amazonChannelFlow.poRows": "{{count}} PO rows",
  "pages.amazonChannelFlow.countryDrivers": "Country Drivers",
  "pages.amazonChannelFlow.countryDriversDescription": "Calendar-period Units and current Open PO position by exact country context.",
  "pages.amazonChannelFlow.modelDrivers": "Model Drivers",
  "pages.amazonChannelFlow.modelDriversDescription": "Country + exact master model comparison. ASIN is retained as evidence and is not a mandatory cross-source join key.",
  "pages.amazonChannelFlow.colAsinEvidence": "ASIN Evidence",
  "pages.amazonChannelFlow.sortHighestInvoicedSellIn": "Highest Invoiced Sell-in",
  "pages.amazonChannelFlow.sortHighestSellOut": "Highest Sell-out",
  "pages.amazonChannelFlow.sortHighestNetFlow": "Highest Net Flow",
  "pages.amazonChannelFlow.sortHighestOpenPo": "Highest Open PO",
  "pages.amazonChannelFlow.alignmentCoverage": "Alignment & Coverage",
  "pages.amazonChannelFlow.alignmentCoverageDescription": "Every authority reports backend-owned row and identity coverage; React does not derive a percentage.",
  "pages.amazonChannelFlow.poSourceCoverage": "PO Source Coverage",
  "pages.amazonChannelFlow.invoiceContextCoverage": "Invoice Context Coverage",
  "pages.amazonChannelFlow.sellOutCoverage": "Sell-out Coverage",
  "pages.amazonChannelFlow.coverageDetail": "{{product}} product · {{customer}} country/customer · {{value}} value unresolved",
  "pages.amazonChannelFlow.dataQualityExceptions": "Data Quality / Exceptions",
  "pages.amazonChannelFlow.dataQualityExceptionsDescription": "Only deterministic source and mapping conditions are listed; positive or negative Net Flow is not automatically classified as risk.",
  "pages.amazonChannelFlow.confirmed": "{{count}} confirmed",
  "pages.amazonChannelFlow.evidence": "Evidence",
  "pages.amazonChannelFlow.evidenceDescription": "Flow KPI → Country / Model contribution → governed PO, Invoice and Sell-out source evidence.",
  "pages.amazonChannelFlow.evidencePo": "PO Intake / Open PO",
  "pages.amazonChannelFlow.evidenceInvoice": "Invoiced Sell-in",
  "pages.amazonChannelFlow.evidenceInvoiceSummary": "Invoice summary amounts",
  "pages.amazonChannelFlow.evidenceSellOut": "Sell-out batches",
  "pages.amazonChannelFlow.evidenceIdentity": "Identity evidence",
  "pages.amazonChannelFlow.loading": "Loading Amazon Channel Flow",
  "pages.amazonChannelFlow.loadingDescription": "Aligning PO, active Invoice and Sell-out authorities at calendar grain.",
  "pages.amazonChannelFlow.unavailable": "Amazon Channel Flow unavailable",
  "pages.amazonChannelFlow.loadingCountryDrivers": "Loading Channel Flow country drivers",
  "pages.amazonChannelFlow.loadingModelDrivers": "Loading Channel Flow model drivers",
  "pages.amazonChannelFlow.qualityFlowProxy": "Flow proxy only; not actual inventory.",
  "pages.amazonChannelFlow.qualityPoFlow": "PO flow; not completed Sell-in.",
  "pages.amazonChannelFlow.qualityInvoiceValueOnly": "Invoice value only; not Sell-out revenue and not used in Units alignment.",
  "pages.amazonChannelFlow.alignmentDenominatorZero": "Not applicable: Invoiced Sell-in denominator is zero.",
  "pages.amazonChannelFlow.alignmentDenominatorNegative": "Blocked: signed denominator is negative.",
  "pages.amazonChannelFlow.alignmentSourceCoverageIncomplete": "Blocked: three-month source coverage is incomplete.",
  "pages.amazonChannelFlow.alignmentThreeMonthsNotAvailable": "Three closed months are not available.",
  "pages.amazonChannelFlow.exceptionPoWithoutInvoice": "PO without Invoice completion",
  "pages.amazonChannelFlow.exceptionInvoiceWithoutPo": "Invoice without PO reference",
  "pages.amazonChannelFlow.exceptionInvoiceModelUnresolved": "Invoice model unresolved",
  "pages.amazonChannelFlow.exceptionPoModelUnresolved": "PO model unresolved",
  "pages.amazonChannelFlow.exceptionCountryContextMismatch": "Country context mismatch",
  "pages.amazonChannelFlow.exceptionSellOutModelUnresolved": "Sell-out model unresolved",
  "pages.amazonChannelFlow.exceptionSourceCoverageIncomplete": "Source coverage incomplete",
  "pages.amazonChannelFlow.exceptionCurrentMonthPartial": "Current month · MTD / Partial Period",
  "pages.amazonChannelFlow.exceptionConfirmedSource": "Confirmed source exception",
  "pages.amazonChannelFlow.contextView": "Channel Flow",
  "pages.amazonChannelFlow.contextPoAuthority": "PO Authority",
  "pages.amazonChannelFlow.contextInvoiceAuthority": "Invoiced Sell-in Authority",
  "pages.amazonChannelFlow.contextSellOutAuthority": "Sell-out Authority",
  "pages.amazonChannelFlow.contextAsOf": "As-of Date",
  "pages.amazonChannelFlow.contextCountry": "Country",
  "pages.amazonChannelFlow.colCountry": "Country",
  "pages.amazonChannelFlow.colModel": "Model",
  "pages.amazonChannelFlow.poEvidence": "PO evidence",
  "pages.amazonChannelFlow.invoiceEvidence": "Invoice evidence",
  "pages.amazonChannelFlow.invoiceSummaryEvidence": "Invoice summary evidence",
  "pages.amazonChannelFlow.sellOutEvidence": "Sell-out evidence",
  "pages.amazonChannelFlow.sellInUnitsMetric": "Invoiced Sell-in Units",
  "pages.amazonChannelFlow.sellOutUnitsMetric": "Sell-out Units",
  "pages.amazonChannelFlow.netChannelFlowMetric": "Net Channel Flow",
  "pages.amazonChannelFlow.poIntakeUnitsMetric": "PO Intake Units",
  "pages.amazonChannelFlow.openPoUnitsMetric": "Open PO Units",
  "pages.amazonChannelFlow.invoicedAmountMetric": "Invoiced Amount",
  "pages.amazonChannelFlow.periodLabelMtd": "{{period}} · MTD / Partial Period",
  "pages.amazonChannelFlow.periodLabelClosed": "{{period}} · Closed Period",
  "pages.amazonChannelFlow.asOfPositionHint": "As-of position · {{date}}",

  // ------------------------------------------------------------------
  // amazon inventory page
  // ------------------------------------------------------------------
  "pages.amazonInventory.tabCommercialBi": "Commercial BI",
  "pages.amazonInventory.tabCpfrPlanning": "CPFR Planning",
  "pages.amazonInventory.breadcrumb": "Amazon · Commercial BI",
  "pages.amazonInventory.title": "Amazon Inventory",
  "pages.amazonInventory.description": "Operational Amazon stock, native weekly Sell-out WOC and supply position by exact Model / ASIN.",
  "pages.amazonInventory.ariaCountry": "Amazon Inventory Country",
  "pages.amazonInventory.ariaModel": "Amazon Inventory Model",
  "pages.amazonInventory.ariaAsin": "Amazon Inventory ASIN",
  "pages.amazonInventory.ariaSnapshot": "Amazon Inventory Snapshot",
  "pages.amazonInventory.ariaSort": "Amazon Inventory Sort",
  "pages.amazonInventory.scopeAllAcceptedCountries": "All accepted countries",
  "pages.amazonInventory.contextAria": "Amazon Inventory business context",
  "pages.amazonInventory.partialAlertTitle": "Inventory coverage is partial",
  "pages.amazonInventory.partialAlertDescription": "Missing four-week Sell-out coverage or ASIN-grain PO evidence remains Missing. No value is inferred from another product, marketplace or reporting week.",
  "pages.amazonInventory.stockOutcomes": "Amazon Stock by Scope",
  "pages.amazonInventory.stockOutcomesDescription": "EU Amazon Stock and Selected Country Amazon Stock are two scopes of one accepted Amazon stock authority. They are never added as independent inventory pools. WOC and every component are backend-owned.",
  "pages.amazonInventory.asOf": "As of",
  "pages.amazonInventory.selectedReportingWeeks": "Selected reporting weeks",
  "pages.amazonInventory.weekCoverage": "Reporting-week coverage",
  "pages.amazonInventory.supplyPosition": "Supply Position",
  "pages.amazonInventory.supplyPositionDescription": "As of {{date}}: current-year PO less exact signed Invoice completion. In Transit uses the same frozen Open PO authority.",
  "pages.amazonInventory.viewChannelFlow": "View Channel Flow",
  "pages.amazonInventory.inventoryTrend": "Inventory Trend",
  "pages.amazonInventory.inventoryTrendDescription": "Only accepted backend snapshot history is plotted; WOC trend stays Not Available because no versioned aggregate WOC series exists.",
  "pages.amazonInventory.loadingTrend": "Loading inventory trend",
  "pages.amazonInventory.trendAria": "Amazon accepted inventory snapshot trend",
  "pages.amazonInventory.trendNotAvailable": "Inventory trend not available",
  "pages.amazonInventory.trendNotAvailableDescription": "The accepted source contains only one snapshot for this scope.",
  "pages.amazonInventory.countryDrivers": "Country Drivers",
  "pages.amazonInventory.countryDriversDescription": "Country is a backend filter. Select all accepted countries or an exact available country such as DE, FR, IT, ES or UK; the broader backend stock position remains a separate column.",
  "pages.amazonInventory.modelAsinDrivers": "Model / ASIN Drivers",
  "pages.amazonInventory.modelAsinDriversDescription": "Exact country + master_model_id + ASIN source identity. Missing, zero and signed values remain distinct.",
  "pages.amazonInventory.sortInventory": "Inventory",
  "pages.amazonInventory.sortPanEu": "Pan-EU",
  "pages.amazonInventory.sortWoc": "WOC",
  "pages.amazonInventory.sortOpenPo": "Open PO",
  "pages.amazonInventory.sortInTransit": "In Transit",
  "pages.amazonInventory.noRowsTitle": "No authoritative inventory rows",
  "pages.amazonInventory.noRowsDescription": "No exact row exists for the selected scope.",
  "pages.amazonInventory.dataQualityExceptions": "Data Quality & Exceptions",
  "pages.amazonInventory.dataQualityExceptionsDescription": "Deterministic backend states only; no frontend threshold or inferred mapping.",
  "pages.amazonInventory.marketplaceInventoryCoverage": "Selected-country stock coverage",
  "pages.amazonInventory.coverageDetail": "{{count}} WOC-covered · {{count2}} PO-covered rows",
  "pages.amazonInventory.evidence": "Evidence",
  "pages.amazonInventory.evidenceDescription": "Business labels lead; source batches and line-level identities remain available behind the evidence paths.",
  "pages.amazonInventory.authorityEvidence": "Authority and source evidence",
  "pages.amazonInventory.inventorySource": "Inventory Source",
  "pages.amazonInventory.sellOutSource": "Sell-out Source",
  "pages.amazonInventory.poInvoiceReconciliation": "PO / Invoice Reconciliation",
  "pages.amazonInventory.identityEvidence": "Identity Evidence",
  "pages.amazonInventory.lineageTitle": "Exact source lineage · {{count}} rows",
  "pages.amazonInventory.colSourceFile": "Source file",
  "pages.amazonInventory.colPoEvidence": "PO evidence",
  "pages.amazonInventory.loading": "Loading Amazon Inventory",
  "pages.amazonInventory.loadingDescription": "Reading accepted stock, WOC and exact PO completion authorities.",
  "pages.amazonInventory.unavailable": "Amazon Inventory unavailable",
  "pages.amazonInventory.colMarketplaceInventory": "Marketplace Inventory",
  "pages.amazonInventory.colPanEu": "Pan-EU",
  "pages.amazonInventory.colCountryAmazonStock": "Country Amazon Stock",
  "pages.amazonInventory.colEuAmazonStock": "EU Amazon Stock",
  "pages.amazonInventory.colTrailing4wSellOut": "Trailing 4W Sell-out",
  "pages.amazonInventory.colAverageWeeklySellOut": "Average Weekly Sell-out",
  "pages.amazonInventory.colWoc": "WOC",
  "pages.amazonInventory.colWocStatus": "WOC Status",
  "pages.amazonInventory.colOpenPo": "Open PO",
  "pages.amazonInventory.colInvoiceCompletion": "Invoice Completion",
  "pages.amazonInventory.colInTransit": "In Transit",
  "pages.amazonInventory.colRisk": "Risk",
  "pages.amazonInventory.colCoverage": "Coverage",
  "pages.amazonInventory.colEvidence": "Evidence",
  "pages.amazonInventory.poEvidenceCell": "{{count}} PO · {{source}}",
  "pages.amazonInventory.contextSupplyAsOf": "Supply as of",
  "pages.amazonInventory.contextAuthority": "Authority",
  "pages.amazonInventory.authorityValue": "Accepted Amazon stock + exact PO completion",
  "pages.amazonInventory.metricMarketplaceInventory": "Marketplace Inventory",
  "pages.amazonInventory.metricPanEuStock": "Pan-EU Stock",
  "pages.amazonInventory.metricCountryAmazonStock": "Selected Country Amazon Stock",
  "pages.amazonInventory.metricEuAmazonStock": "EU Amazon Stock",
  "pages.amazonInventory.metricTrailing4wSellOut": "Trailing 4W Sell-out",
  "pages.amazonInventory.metricAverageWeeklySellOut": "Average Weekly Sell-out",
  "pages.amazonInventory.metricWoc": "WOC (Inventory Weeks)",
  "pages.amazonInventory.sparseZeroTitle": "Approved sparse-week zero for WOC",
  "pages.amazonInventory.sparseZeroNote": "Within a complete authoritative reporting week, no reported Sell-out for an exact inventory identity is treated as 0 for WOC. Missing weeks and unresolved identities remain Missing.",
  "pages.amazonInventory.wocCoverageEvidence": "WOC coverage evidence",
  "pages.amazonInventory.wocCoverageCounts": "{{rows}} inventory rows · {{source}} source identity-weeks · {{sparse}} sparse-zero identity-weeks · {{unresolved}} unresolved identity-weeks",
  "pages.amazonInventory.metricCurrentPo": "Current PO",
  "pages.amazonInventory.metricInvoiceCompletion": "Invoice Completion",
  "pages.amazonInventory.metricOpenPo": "Open PO",
  "pages.amazonInventory.metricInTransit": "In Transit",
  "pages.amazonInventory.marketplaceBadge": "Marketplace: {{status}}",
  "pages.amazonInventory.panEuBadge": "Pan-EU: {{status}}",
  "pages.amazonInventory.wocBadge": "WOC: {{status}}",

  // ------------------------------------------------------------------
  // joybuy sales page
  // ------------------------------------------------------------------
  "pages.joybuySales.viewTitle": "Joybuy Sales View",
  "pages.joybuySales.viewDescription": "Sales Alignment and Invoice Actual retain independent reporting state.",
  "pages.joybuySales.salesAlignment": "Sales Alignment",
  "pages.joybuySales.invoiceActual": "Invoice Actual",
  "pages.joybuySales.ariaView": "Joybuy Sales View",
  "pages.joybuySales.breadcrumb": "Joybuy · Commercial BI · Invoiced Sell-in",
  "pages.joybuySales.title": "Joybuy Invoiced Sales",
  "pages.joybuySales.description": "Canonical Invoice outcomes by Issue Date, Country and exact Model, using governed SAP and PDF evidence under accepted authority and dedup rules. Sell-out, Traffic and Conversion remain in Joybuy Operation.",
  "pages.joybuySales.ariaYear": "Joybuy Sales Year",
  "pages.joybuySales.ariaGrain": "Joybuy Sales Grain",
  "pages.joybuySales.ariaPeriod": "Joybuy Sales Period",
  "pages.joybuySales.ariaCountry": "Joybuy Sales Country",
  "pages.joybuySales.ariaModel": "Joybuy Sales Model",
  "pages.joybuySales.ariaFocOnly": "Joybuy Sales Free of Charge Only",
  "pages.joybuySales.ariaUnresolvedOnly": "Joybuy Sales Unresolved Only",
  "pages.joybuySales.focOnly": "FOC only",
  "pages.joybuySales.contextAria": "Joybuy Sales business context",
  "pages.joybuySales.sellInOnlyTitle": "Invoiced Sell-in only",
  "pages.joybuySales.sellInOnlyDescription": "Governed SAP and PDF evidence contributes only through accepted source authority and exact dedup rules. For downstream Sell-out, Traffic and Conversion, {{link}}.",
  "pages.joybuySales.viewJoybuyOperation": "view Joybuy Operation",
  "pages.joybuySales.invoicedOutcomes": "Invoiced Sales Outcomes",
  "pages.joybuySales.invoicedOutcomesDescription": "Zero is a covered business fact; Missing remains Missing. Revenue is EUR only when the reporting authority resolves the line.",
  "pages.joybuySales.periodTrend": "Period Trend",
  "pages.joybuySales.periodTrendDescription": "Backend calendar rollup by authoritative Invoice Issue Date. Empty periods, including April when absent, remain Missing rather than zero.",
  "pages.joybuySales.loadingTrend": "Loading invoiced sales trend",
  "pages.joybuySales.trendAria": "Joybuy invoiced units and revenue trend",
  "pages.joybuySales.countryDrivers": "Country Drivers",
  "pages.joybuySales.countryDriversDescription": "Only countries present in canonical Joybuy invoice facts are shown; Operation geography never expands this scope.",
  "pages.joybuySales.loadingCountryDrivers": "Loading country drivers",
  "pages.joybuySales.countryDriversAria": "Joybuy invoiced sales country drivers",
  "pages.joybuySales.noCountryFacts": "No country facts",
  "pages.joybuySales.noCountryFactsDescription": "No authoritative invoice lines exist for this period and filter.",
  "pages.joybuySales.modelDrivers": "Model Drivers",
  "pages.joybuySales.modelDriversDescription": "Exact Country + master_model_id grain. Unresolved identity stays visible and filterable.",
  "pages.joybuySales.noModelFacts": "No model facts",
  "pages.joybuySales.noModelFactsDescription": "No authoritative model rows exist for this period and filter.",
  "pages.joybuySales.coverageQuality": "Invoice Coverage & Quality",
  "pages.joybuySales.coverageQualityDescription": "Latest Invoice Issue Date: {{date}}. Active and superseded evidence remain distinct.",
  "pages.joybuySales.revenueCoverage": "Revenue coverage",
  "pages.joybuySales.productResolution": "Product resolution",
  "pages.joybuySales.countryCoverage": "Country coverage",
  "pages.joybuySales.coverageDetailFoc": "{{count}} explicit FOC · {{missing}} Missing",
  "pages.joybuySales.unresolvedCount": "{{count}} unresolved",
  "pages.joybuySales.yearEvidence": "Year evidence: {{activeDocs}} active documents / {{activeLines}} active lines · {{supersededDocs}} superseded documents / {{supersededLines}} superseded lines · {{replayRuns}} replay runs · {{signed}} signed lines in selected scope.",
  "pages.joybuySales.exceptions": "Exceptions",
  "pages.joybuySales.exceptionsDescription": "Deterministic Missing or unresolved authority only. Explicit FOC and signed legal facts are classifications, not automatic errors.",
  "pages.joybuySales.evidence": "Evidence",
  "pages.joybuySales.evidenceDescription": "Invoice document, active line, raw PDF amount and business-resolved revenue remain traceable together.",
  "pages.joybuySales.authorityBoundaries": "Authority and source boundaries",
  "pages.joybuySales.bulkReconciliationContribution": "Bulk reconciliation contribution: {{value}}",
  "pages.joybuySales.invoiceUploadHistory": "Invoice upload history",
  "pages.joybuySales.identityEvidence": "Identity evidence",
  "pages.joybuySales.invoiceDocuments": "Invoice documents · {{count}}",
  "pages.joybuySales.loading": "Loading Joybuy Invoiced Sales",
  "pages.joybuySales.loadingDescription": "Reading governed canonical Invoice and InvoiceLine authorities.",
  "pages.joybuySales.unavailable": "Joybuy Sales unavailable",
  "pages.joybuySales.colInvoice": "Invoice",
  "pages.joybuySales.colIssueDate": "Issue Date",
  "pages.joybuySales.colCustomer": "Customer",
  "pages.joybuySales.colCountry": "Country",
  "pages.joybuySales.colUnits": "Units",
  "pages.joybuySales.colRevenueEur": "Revenue EUR",
  "pages.joybuySales.colLines": "Lines",
  "pages.joybuySales.colFoc": "FOC",
  "pages.joybuySales.colMissingRevenue": "Missing Revenue",
  "pages.joybuySales.colSourcePdf": "Source PDF",
  "pages.joybuySales.colLine": "Line",
  "pages.joybuySales.colPdfPageRow": "PDF page / row",
  "pages.joybuySales.colSku": "SKU",
  "pages.joybuySales.colModel": "Model",
  "pages.joybuySales.colQuantity": "Quantity",
  "pages.joybuySales.colRawPdfAmount": "Raw PDF amount",
  "pages.joybuySales.colReportingEur": "Reporting EUR",
  "pages.joybuySales.colRevenueStatus": "Revenue status",
  "pages.joybuySales.colInvoicedUnits": "Invoiced Units",
  "pages.joybuySales.colRevenueEurDriver": "Revenue EUR",
  "pages.joybuySales.colInvoices": "Invoices",
  "pages.joybuySales.colRevenueCoverage": "Revenue Coverage",
  "pages.joybuySales.contextPeriod": "Issue-date period",
  "pages.joybuySales.contextPeriodState": "Period state",
  "pages.joybuySales.contextCountry": "Country",
  "pages.joybuySales.contextModel": "Model",
  "pages.joybuySales.contextAuthority": "Source authority",
  "pages.joybuySales.metricInvoicedUnits": "Invoiced Units",
  "pages.joybuySales.metricInvoicedRevenue": "Invoiced Revenue",
  "pages.joybuySales.metricInvoiceDocuments": "Invoice Documents",
  "pages.joybuySales.metricInvoiceLines": "Invoice Lines",
  "pages.joybuySales.evidenceInvoice": "Invoice evidence",

  // ------------------------------------------------------------------
  // joybuy sales alignment
  // ------------------------------------------------------------------
  "pages.joybuyAlignment.breadcrumb": "Joybuy · Commercial BI",
  "pages.joybuyAlignment.title": "Joybuy Sales Alignment",
  "pages.joybuyAlignment.description": "Canonical invoiced Sell-in versus Joybuy Operational Sell-out at exact Country + Model scope.",
  "pages.joybuyAlignment.ariaYear": "Joybuy Alignment Year",
  "pages.joybuyAlignment.ariaGrain": "Joybuy Alignment Grain",
  "pages.joybuyAlignment.ariaPeriod": "Joybuy Alignment Period",
  "pages.joybuyAlignment.ariaCountry": "Joybuy Alignment Country",
  "pages.joybuyAlignment.ariaModel": "Joybuy Alignment Model",
  "pages.joybuyAlignment.ariaCountrySort": "Joybuy Alignment Country Sort",
  "pages.joybuyAlignment.ariaModelSort": "Joybuy Alignment Model Sort",
  "pages.joybuyAlignment.contextAria": "Joybuy Sales Alignment business context",
  "pages.joybuyAlignment.mtdAlertTitle": "Current Month · MTD / Partial Period",
  "pages.joybuyAlignment.mtdAlertDescription": "The current month remains partial and is excluded from Rolling 3 Closed Months Alignment.",
  "pages.joybuyAlignment.comparableScopeTitle": "Comparable scope only",
  "pages.joybuyAlignment.comparableScopeDescription": "Net Channel Flow and 3M Alignment use exact Country + master model matches. Missing authority is never converted to zero.",
  "pages.joybuyAlignment.alignmentOutcomes": "Alignment Outcomes",
  "pages.joybuyAlignment.alignmentOutcomesDescription": "Net Channel Flow is Invoiced Sell-in less Sell-out in comparable scope; it is a flow proxy, not actual inventory.",
  "pages.joybuyAlignment.primaryOutcomes": "Primary Outcomes",
  "pages.joybuyAlignment.supporting": "Supporting",
  "pages.joybuyAlignment.sellInSellOutTrend": "Sell-in vs Sell-out Trend",
  "pages.joybuyAlignment.sellInSellOutTrendDescription": "Backend calendar rollup. Missing creates a gap, true zero remains zero, signed values remain signed.",
  "pages.joybuyAlignment.loadingTrend": "Loading alignment trend",
  "pages.joybuyAlignment.trendAria": "Joybuy Sell-in versus Sell-out trend",
  "pages.joybuyAlignment.colPeriod": "Period",
  "pages.joybuyAlignment.colStatus": "Status",
  "pages.joybuyAlignment.colInvoicedSellIn": "Invoiced Sell-in",
  "pages.joybuyAlignment.colSellOut": "Sell-out",
  "pages.joybuyAlignment.colNetChannelFlow": "Net Channel Flow",
  "pages.joybuyAlignment.countryDrivers": "Country Drivers",
  "pages.joybuyAlignment.countryDriversDescription": "Countries without both exact authorities remain non-comparable; their missing side is displayed as Missing.",
  "pages.joybuyAlignment.modelDrivers": "Model Drivers",
  "pages.joybuyAlignment.modelDriversDescription": "Comparison key is exact Country + master_model_id. ASIN is not used as a join key.",
  "pages.joybuyAlignment.col3mAlignment": "3M Alignment",
  "pages.joybuyAlignment.colComparable": "Comparable",
  "pages.joybuyAlignment.colCoverage": "Coverage",
  "pages.joybuyAlignment.colEvidence": "Evidence",
  "pages.joybuyAlignment.coverageCell": "{{sellIn}} · {{sellOut}}",
  "pages.joybuyAlignment.evidenceCell": "{{count}} Sell-in · {{count2}} Sell-out",
  "pages.joybuyAlignment.sortHighestSellIn": "Highest Sell-in",
  "pages.joybuyAlignment.sortHighestSellOut": "Highest Sell-out",
  "pages.joybuyAlignment.sortHighestNetFlow": "Highest Net Flow",
  "pages.joybuyAlignment.sortHighestAlignment": "Highest Alignment",
  "pages.joybuyAlignment.coverageQuality": "Coverage & Quality",
  "pages.joybuyAlignment.coverageQualityDescription": "Coverage and comparable scope are backend-owned.",
  "pages.joybuyAlignment.exactPairs": "{{count}} exact pairs",
  "pages.joybuyAlignment.nonComparable": "{{countries}} non-comparable countries",
  "pages.joybuyAlignment.noNonComparable": "No non-comparable countries",
  "pages.joybuyAlignment.exceptions": "Exceptions",
  "pages.joybuyAlignment.exceptionsDescription": "Deterministic authority, scope, mapping and partial-period facts only.",
  "pages.joybuyAlignment.evidence": "Evidence",
  "pages.joybuyAlignment.evidenceDescription": "Sell-in and Sell-out retain separate lineage; Alignment links both authorities.",
  "pages.joybuyAlignment.sellInInvoiceEvidence": "Sell-in Invoice evidence",
  "pages.joybuyAlignment.sellOutOperationEvidence": "Sell-out Operation evidence",
  "pages.joybuyAlignment.identityEvidence": "Identity evidence",
  "pages.joybuyAlignment.viewTrafficConversion": "View Traffic & Conversion",
  "pages.joybuyAlignment.viewInventory": "View Inventory",
  "pages.joybuyAlignment.loading": "Loading Joybuy Sales Alignment",
  "pages.joybuyAlignment.loadingDescription": "Aligning canonical invoiced Sell-in with governed Operational Sell-out.",
  "pages.joybuyAlignment.unavailable": "Joybuy Sales Alignment unavailable",
  "pages.joybuyAlignment.metricInvoicedSellInUnits": "Invoiced Sell-in Units",
  "pages.joybuyAlignment.metricSellOutUnits": "Sell-out Units",
  "pages.joybuyAlignment.metricNetChannelFlow": "Net Channel Flow",
  "pages.joybuyAlignment.metric3mSellThrough": "3M Sell-through Alignment",
  "chart.netChannelFlow": "Net Channel Flow",
  "status.unavailable": "Unavailable",
  "pages.joybuyAlignment.colCountry": "Country",
  "pages.joybuyAlignment.colModel": "Model",
  "pages.joybuyAlignment.colSellInMapping": "Sell-in Mapping",
  "pages.joybuyAlignment.colSellOutMapping": "Sell-out Mapping",

  "pages.joybuyAlignment.qualityFlowProxy": "Flow proxy only; not actual inventory.",
  "pages.joybuyAlignment.contextView": "Sales Alignment",
  "pages.joybuyAlignment.contextSellInAuthority": "Sell-in Authority",
  "pages.joybuyAlignment.contextSellOutAuthority": "Sell-out Authority",
  "pages.joybuyAlignment.contextComparableScope": "Comparable Scope",
  "pages.joybuyAlignment.contextAsOf": "As-of Date",
  "pages.joybuyAlignment.unavailableScope": "Unavailable",
  "pages.joybuyAlignment.evidenceAlignment": "Alignment evidence",

  // ------------------------------------------------------------------
  // joybuy inventory page
  // ------------------------------------------------------------------
  "pages.joybuyInventory.breadcrumb": "Joybuy · Commercial BI",
  "pages.joybuyInventory.title": "Joybuy Inventory",
  "pages.joybuyInventory.description": "Current Stock, Transit position and exact source-SKU evidence from the accepted Weekly Operation authority.",
  "pages.joybuyInventory.ariaCountry": "Joybuy Inventory Country",
  "pages.joybuyInventory.ariaModel": "Joybuy Inventory Model",
  "pages.joybuyInventory.ariaSourceSku": "Joybuy Inventory Source SKU",
  "pages.joybuyInventory.ariaSnapshot": "Joybuy Inventory Snapshot",
  "pages.joybuyInventory.ariaWocState": "Joybuy Inventory WOC State",
  "pages.joybuyInventory.ariaSort": "Joybuy Inventory Sort",
  "pages.joybuyInventory.contextAria": "Joybuy Inventory business context",
  "pages.joybuyInventory.partialAlertTitle": "Inventory coverage is partial",
  "pages.joybuyInventory.partialAlertDescription": "Unresolved identity and source Missing values remain explicit; no historical snapshot, legacy context or missing value is added in React.",
  "pages.joybuyInventory.inventoryOutcomes": "Inventory Outcomes",
  "pages.joybuyInventory.inventoryOutcomesDescription": "Stock, Position, Supporting and Quality have distinct visual roles; there is no frontend Total Available calculation.",
  "pages.joybuyInventory.primaryStock": "Primary Stock",
  "pages.joybuyInventory.position": "Position",
  "pages.joybuyInventory.supportingQuality": "Supporting & Quality",
  "pages.joybuyInventory.stockTransitPosition": "Stock & Transit Position",
  "pages.joybuyInventory.stockTransitPositionDescription": "In Transit is a current position, not a Sell-in/Sell-out flow. Backend reconciliation keeps the unresolved bucket in the headline.",
  "pages.joybuyInventory.headline": "Headline {{value}}",
  "pages.joybuyInventory.mapped": "Mapped {{value}}",
  "pages.joybuyInventory.unresolvedValue": "Unresolved {{value}}",
  "pages.joybuyInventory.trendTitle": "Authoritative Inventory Trend",
  "pages.joybuyInventory.trendDescription": "One point per accepted reporting snapshot, chronological ascending. Gaps remain gaps and historical generations are never summed.",
  "pages.joybuyInventory.loadingTrend": "Loading inventory trend",
  "pages.joybuyInventory.trendAria": "Joybuy accepted inventory snapshot trend",
  "pages.joybuyInventory.noTrendTitle": "No authoritative trend in this scope",
  "pages.joybuyInventory.countryDrivers": "Country Drivers",
  "pages.joybuyInventory.countryDriversDescription": "Exact site/country facts from the selected backend snapshot; use table headers to sort On Hand, In Transit, WOC or Unresolved Transit.",
  "pages.joybuyInventory.modelSkuDrivers": "Model / SKU Drivers",
  "pages.joybuyInventory.modelSkuDriversDescription": "Exact Country + master_model_id, or unresolved source SKU, at the selected snapshot. Zero, Missing and signed values remain distinct.",
  "pages.joybuyInventory.sortOnHand": "On Hand",
  "pages.joybuyInventory.sortInTransit": "In Transit",
  "pages.joybuyInventory.sortWoc": "WOC",
  "pages.joybuyInventory.sortUnresolved": "Unresolved",
  "pages.joybuyInventory.noRowsTitle": "No authoritative inventory rows",
  "pages.joybuyInventory.noRowsDescription": "No exact row exists for the selected backend scope.",
  "pages.joybuyInventory.coverageFreshness": "Coverage & Freshness",
  "pages.joybuyInventory.coverageFreshnessDescription": "Coverage and freshness are backend fields. No staleness threshold has been invented.",
  "pages.joybuyInventory.onHandCoverage": "On Hand coverage",
  "pages.joybuyInventory.inTransitCoverage": "In Transit coverage",
  "pages.joybuyInventory.wocCoverage": "WOC coverage",
  "pages.joybuyInventory.exactMappingCoverage": "Exact mapping coverage",
  "pages.joybuyInventory.mappingCoverageDetail": "{{count}} unresolved rows · {{freshness}}",
  "pages.joybuyInventory.exceptions": "Exceptions",
  "pages.joybuyInventory.exceptionsDescription": "Deterministic source and coverage states only; no new stock-risk threshold.",
  "pages.joybuyInventory.evidence": "Evidence",
  "pages.joybuyInventory.evidenceDescription": "Business evidence paths preserve snapshot, mapping and source lineage without exposing raw DTOs in the main view.",
  "pages.joybuyInventory.authorityEvidence": "Authority and source evidence",
  "pages.joybuyInventory.weeklyOperationSource": "Weekly Operation source",
  "pages.joybuyInventory.identityMapping": "Identity mapping",
  "pages.joybuyInventory.snapshotLineage": "Snapshot lineage",
  "pages.joybuyInventory.lineageTitle": "Exact contribution lineage · {{count}} rows",
  "pages.joybuyInventory.colState": "State",
  "pages.joybuyInventory.viewSalesAlignment": "View Sales Alignment",
  "pages.joybuyInventory.viewTrafficConversion": "View Traffic & Conversion",
  "pages.joybuyInventory.loading": "Loading Joybuy Inventory",
  "pages.joybuyInventory.loadingDescription": "Reading one accepted inventory snapshot and compact backend-owned coverage.",
  "pages.joybuyInventory.unavailable": "Joybuy Inventory unavailable",
  "pages.joybuyInventory.colSourceSku": "Source SKU",
  "pages.joybuyInventory.colCountry": "Country",
  "pages.joybuyInventory.colModel": "Model",

  "pages.joybuyInventory.colCustomerOnHand": "Customer On Hand",
  "pages.joybuyInventory.colInTransit": "In Transit",
  "pages.joybuyInventory.colWoc": "WOC",
  "pages.joybuyInventory.colWocStatus": "WOC Status",
  "pages.joybuyInventory.colMapping": "Mapping",
  "pages.joybuyInventory.colSnapshot": "Snapshot",
  "pages.joybuyInventory.colCoverage": "Coverage",
  "pages.joybuyInventory.colEvidence": "Evidence",
  "pages.joybuyInventory.colUnresolvedTransit": "Unresolved Transit",
  "pages.joybuyInventory.colMappedUnresolved": "Mapped / Unresolved",
  "pages.joybuyInventory.colSource": "Source",
  "pages.joybuyInventory.colSourceRow": "Source row",
  "pages.joybuyInventory.metricCustomerOnHand": "Customer On Hand",
  "pages.joybuyInventory.metricInTransit": "In Transit",
  "pages.joybuyInventory.metricWoc": "WOC (30d)",
  "pages.joybuyInventory.metricUnresolvedTransit": "Unresolved Transit",
  "pages.joybuyInventory.mappedPlusUnresolved": "{{mapped}} mapped + {{unresolved}} unresolved",
  "pages.joybuyInventory.contextView": "Inventory Position",
  "pages.joybuyInventory.contextInventoryAuthority": "Inventory Authority",
  "pages.joybuyInventory.contextTransitAuthority": "Transit Authority",
  "pages.joybuyInventory.contextWocAuthority": "WOC Authority",
  "pages.joybuyInventory.contextSourceFreshness": "Source Freshness",
  "pages.joybuyInventory.contextMappingCoverage": "Mapping Coverage",
  "pages.joybuyInventory.authorityWeeklyOperation": "Weekly Operation",
  "pages.joybuyInventory.trendBadge": "{{label}}: {{value}}",

  // ------------------------------------------------------------------
  // joybuy operation page
  // ------------------------------------------------------------------
  "pages.joybuyOperation.breadcrumb": "Joybuy · Commercial BI",
  "pages.joybuyOperation.title": "Joybuy Operation",
  "pages.joybuyOperation.description": "Model Traffic, backend Orders / Traffic Conversion and authoritative Sell-out—kept separate from Inventory, WOC, Ads and Pricing.",
  "pages.joybuyOperation.ariaPeriod": "Joybuy Operation Period",
  "pages.joybuyOperation.ariaCountry": "Joybuy Operation Country",
  "pages.joybuyOperation.ariaModel": "Joybuy Operation Model",
  "pages.joybuyOperation.ariaConversionState": "Joybuy Operation Conversion State",
  "pages.joybuyOperation.ariaSellOutState": "Joybuy Operation Sell-out State",
  "pages.joybuyOperation.ariaSort": "Joybuy Operation Sort",
  "pages.joybuyOperation.contextAria": "Joybuy Operation business context",
  "pages.joybuyOperation.partialAlertTitle": "Operation source coverage is partial",
  "pages.joybuyOperation.partialAlertDescription": "Missing Orders or Traffic and unresolved product identities remain explicit. Conversion uses only authoritative paired Orders / Traffic rows; a real zero remains covered.",
  "pages.joybuyOperation.operationOutcomes": "Operation Outcomes",
  "pages.joybuyOperation.operationOutcomesDescription": "Backend-owned current-period values. Conversion is SUM Orders / SUM Traffic over authoritative paired rows and remains Missing when inputs or the Traffic denominator are absent.",
  "pages.joybuyOperation.trafficSellOutTrend": "Traffic & Sell-out Trend",
  "pages.joybuyOperation.trafficSellOutTrendDescription": "Real parser-validated reporting dates. Traffic uses the left visitor axis; Sell-out uses the right Units axis; Missing remains a gap.",
  "pages.joybuyOperation.loadingTrafficTrend": "Loading Traffic and Sell-out trend",
  "pages.joybuyOperation.trafficTrendAria": "Joybuy Traffic and Sell-out trend with separate axes",
  "pages.joybuyOperation.conversionTrend": "Conversion Trend",
  "pages.joybuyOperation.conversionTrendDescription": "Independent backend Orders / Traffic series at the active country scope; no React ratio and one point per reporting period.",
  "pages.joybuyOperation.loadingConversionTrend": "Loading Conversion trend",
  "pages.joybuyOperation.conversionTrendAria": "Joybuy Orders over Traffic Conversion trend",
  "pages.joybuyOperation.conversionMissingTitle": "Conversion is Missing in this scope",
  "pages.joybuyOperation.conversionMissingDescription": "Authoritative paired Orders and model Traffic are required; Missing Traffic is never reconstructed from the source conversion rate.",
  "pages.joybuyOperation.countryDrivers": "Country Drivers",
  "pages.joybuyOperation.countryDriversDescription": "Exact Operation site context from the backend; no hard-coded country list.",
  "pages.joybuyOperation.modelSkuDrivers": "Model / SKU Drivers",
  "pages.joybuyOperation.modelSkuDriversDescription": "Selected period + exact country + source SKU + exact MasterModel when resolved. Each Conversion is calculated in the backend from that model row's Orders / Traffic; source Conversion remains evidence only.",
  "pages.joybuyOperation.sortSellOut": "Sell-out",
  "pages.joybuyOperation.sortTraffic": "Traffic",
  "pages.joybuyOperation.sortConversion": "Conversion",
  "pages.joybuyOperation.sortUnresolved": "Unresolved",
  "pages.joybuyOperation.noRowsTitle": "No authoritative Operation rows",
  "pages.joybuyOperation.noRowsDescription": "No source row matches the selected backend filters.",
  "pages.joybuyOperation.funnelRelationship": "Funnel & Period Relationship",
  "pages.joybuyOperation.funnelRelationshipDescription": "Neutral period-over-period comparisons supplied by the backend. They are descriptive deltas, not causal attribution.",
  "pages.joybuyOperation.previousCurrent": "Previous {{previous}} · Current {{current}}",
  "pages.joybuyOperation.coverageFreshness": "Coverage & Freshness",
  "pages.joybuyOperation.coverageFreshnessDescription": "Coverage and freshness are backend fields. No stale threshold has been approved or invented.",
  "pages.joybuyOperation.trafficCoverage": "Traffic coverage",
  "pages.joybuyOperation.trafficCoverageDetail": "{{source}} source · {{derived}} approved fallback",
  "pages.joybuyOperation.conversionCoverage": "Conversion coverage",
  "pages.joybuyOperation.sellOutCoverage": "Sell-out coverage",
  "pages.joybuyOperation.exactMappingCoverage": "Exact mapping coverage",
  "pages.joybuyOperation.mappingCoverageDetail": "{{count}} unresolved · {{freshness}}",
  "pages.joybuyOperation.exceptions": "Exceptions",
  "pages.joybuyOperation.exceptionsDescription": "Deterministic data-quality and source states only; no low-Traffic, low-Conversion or causal risk threshold.",
  "pages.joybuyOperation.colState": "State",
  "pages.joybuyOperation.colAffectedRows": "Affected Rows",
  "pages.joybuyOperation.colEvidence": "Evidence",
  "pages.joybuyOperation.colSampleKeys": "Sample Keys",
  "pages.joybuyOperation.evidence": "Evidence",
  "pages.joybuyOperation.evidenceDescription": "Authority, period, identity and source-row lineage remain auditable without exposing batch UUIDs in the main view.",
  "pages.joybuyOperation.authoritySemanticEvidence": "Authority and semantic evidence",
  "pages.joybuyOperation.weeklyOperationSource": "Weekly Operation source",
  "pages.joybuyOperation.lineageTitle": "Exact source-row lineage · {{count}} rows",
  "pages.joybuyOperation.colSourceRow": "Source row",
  "pages.joybuyOperation.crossLinks": "Cross-links",
  "pages.joybuyOperation.crossLinksDescription": "Open adjacent authorities without copying Sales Alignment or Inventory formulas into Operation.",
  "pages.joybuyOperation.loading": "Loading Joybuy Operation",
  "pages.joybuyOperation.loadingDescription": "Reading the compact Traffic, Conversion and Sell-out authority projection.",
  "pages.joybuyOperation.unavailable": "Joybuy Operation unavailable",
  "pages.joybuyOperation.colTrafficUv7d": "Traffic · UV 7d",
  "pages.joybuyOperation.colSellOut7d": "Sell-out · 7d",
  "pages.joybuyOperation.colConversion": "Conversion",
  "pages.joybuyOperation.colSourceSkus": "Source SKUs",
  "pages.joybuyOperation.colUnresolved": "Unresolved",
  "pages.joybuyOperation.colModel": "Model",
  "pages.joybuyOperation.colPeriod": "Period",

  "pages.joybuyOperation.colSourceSku": "Source SKU",
  "pages.joybuyOperation.colCountry": "Country",
  "pages.joybuyOperation.colSourceTraffic": "Source Traffic",
  "pages.joybuyOperation.colTrafficStatus": "Traffic Status",
  "pages.joybuyOperation.colConversionOrdersTraffic": "Conversion · Orders / Traffic",
  "pages.joybuyOperation.colConversionStatus": "Conversion Status",
  "pages.joybuyOperation.colSourceConversionRatio": "Source Conversion Ratio",
  "pages.joybuyOperation.colSellOutStatus": "Sell-out Status",
  "pages.joybuyOperation.colMapping": "Mapping",
  "pages.joybuyOperation.colSource": "Source",
  "pages.joybuyOperation.metricTraffic": "Traffic · UV 7d",
  "pages.joybuyOperation.metricConversion": "Conversion · 7d",
  "pages.joybuyOperation.metricSellOut": "Sell-out · 7d",
  "pages.joybuyOperation.contextView": "Traffic · Conversion · Sell-out",
  "pages.joybuyOperation.contextGrain": "Grain",
  "pages.joybuyOperation.contextFreshness": "Freshness",
  "pages.joybuyOperation.grainValue": "Period + Country + Source SKU",
  "pages.joybuyOperation.selectedModel": "Selected Model",
  "pages.joybuyOperation.allModels": "All Models",
  "pages.joybuyOperation.allConversionStates": "All Conversion states",
  "pages.joybuyOperation.allSellOutStates": "All Sell-out states",
  "pages.joybuyOperation.allWocStates": "All WOC states",
  "pages.joybuyOperation.realZero": "Real zero",
  "parity.calculateDraft": "Calculate Draft",
  "parity.saveFreeze": "Save / Freeze",
  "parity.compareCurrent": "Compare Current",
  "parity.selectedFrozenRun": "Selected Frozen Run",
  "parity.frozenRunPlaceholder": "Select frozen run",
  "parity.uatActor": "UAT audit actor",
  "parity.proposalRunTitle": "Proposal Calculation / Reference Run",
  "parity.proposalContext": "Proposal / contract context",
  "parity.calculateReview": "Calculate / Review",
  "parity.generateReference": "Generate Reference Run",
  "parity.refreshRuns": "Refresh Runs",
  "parity.joybuyMixTitle": "Model Mix & Loading Decisions",
  "parity.joybuyMixDescription": "Each selected exact master_model_id is one model-mix line. Capacity, utilization and remaining pallets come from the governed loading profile; no screen-size inference is used.",
};
